import { format } from "date-fns";
import { useEffect, useState } from "react";
import { DataGrid, IconButton, Tooltip } from "../../components";
import { PermisoesHelper } from "../../helpers";
import { ListLayout } from "../../layouts";
import {
  history,
  ManifestacaoDestinatarioService,
  NfeService,
  notification,
  SessionService,
} from "../../services";
import CertificadoDigitalService from "../../services/api/certificado-digital.service";
import {
  formatCnpj,
  formatDate,
  formatPrice,
  responseErros,
} from "../../utils";
import ButtonFiltroAvancado from "./components/button-filtro-avancado.component";
import { situacoes } from "./components/constantes";
import FormFiltroAvancadoManifestacao from "./dialog/form-filtro-avancado-manifestacao.dialog";
import { renderSituacoes } from "./manifestacao-destinatario";
import ButtonMenu from "./components/button-menu.component";
import RegistroEventoDialog from "./dialog/registro-evento.dialog";

const manifestacaoDestinatarioService = new ManifestacaoDestinatarioService();
const certificadoDigitalService = new CertificadoDigitalService();
const nfeService = new NfeService();
const sessionService = new SessionService();

const ManifestacaoDestinatarioView = () => {
  const permissoesHelper = new PermisoesHelper();
  permissoesHelper.validarPermisao("manifestacao-destinatario-visualizar");
  const [loading, setLoading] = useState(false);
  const [notasManifestacao, setNotasManifestacao] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);
  const [rowCount, setRowCount] = useState(null);
  const [openRegistroEventoDialog, setOpenRegistroEventoDialog] =
    useState(false);

  const [buscaFiltro, setBuscaFiltro] = useState("");
  const empresa = sessionService.getEmpresa();

  const colunas = [
    {
      field: "situacao",
      headerName: "Situação",
      renderCell: (params) => renderSituacoes(params.value, situacoes),
      width: 300,
    },
    {
      field: "cnpj",
      headerName: "CNPJ",
      valueGetter: (params) => formatCnpj(params.value),
      width: 200,
    },
    {
      field: "razaoSocial",
      headerName: "Fornecedor",
      sortable: false,
      width: 400,
      filtrar: true,
    },
    {
      field: "inscricaoEstadual",
      headerName: "Inscrição Estadual",
      sortable: false,
      width: 200,
    },
    {
      field: "dataEmissao",
      headerName: "Data Emissão",
      valueGetter: (params) =>
        format(formatDate.received(params.value), "dd/MM/yyyy"),
      width: 150,
    },
    {
      field: "valorTotal",
      headerName: "Valor Total",
      headerAlign: "right",
      align: "right",
      valueGetter: (params) => formatPrice(params.value),
      width: 150,
    },
    {
      field: "chaveXml",
      headerName: "Chave XML",
      sortable: false,
      width: 400,
    },
    {
      field: "acoes",
      headerName: "Ações",
      headerAlign: "center",
      sortable: false,
      width: 150,
      renderCell: (params) => (
        <div className="d-flex w-100 justify-content-center align-items-center">
          {params.row.situacao === "CIENCIA" ? (
            <>
              <IconButton onClick={() => onClickBaixarXml(params.row.chaveXml)}>
                <Tooltip title="Baixar XML" arrow placement="top">
                  <i
                    className="ph-bold ph-download-simple"
                    style={{ color: "#494C62", fontSize: 18 }}
                  ></i>
                </Tooltip>
              </IconButton>
              <IconButton
                onClick={() => onClickBaixarDanfe(params.row.chaveXml)}
              >
                <Tooltip title="Visualizar DANFE" arrow placement="top">
                  <i
                    className="ph-bold ph-file-arrow-up"
                    style={{ color: "#494C62", fontSize: 18 }}
                  ></i>
                </Tooltip>
              </IconButton>
              <div>{renderIconImportacao(params)}</div>
            </>
          ) : (
            <>
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  setSelectionModel([params.id]);
                  handleRegistrarEvento();
                }}
              >
                <Tooltip title="Registrar Evento" arrow placement="top">
                  <i
                    className="ph-bold ph-upload-simple"
                    style={{ color: "#494C62", fontSize: 18 }}
                  ></i>
                </Tooltip>
              </IconButton>
            </>
          )}
        </div>
      ),
    },
  ];

  useEffect(() => {
    buscarManifestacaoDestinatario();
  }, []);

  const renderIconImportacao = (params) => {
    return params.row.notaExistente ? (
      <IconButton onClick={() => onClickViewNfEntrada(params.row.nfEntradaId)}>
        <Tooltip title="Visualizar Nota de Entrada" arrow placement="top">
          <i
            className="ph-fill ph-eye"
            style={{ color: "#494C62", fontSize: 18 }}
          ></i>
        </Tooltip>
      </IconButton>
    ) : (
      <IconButton onClick={() => onClickImportarNfEntrada(params.row.chaveXml)}>
        <Tooltip title="Importar Nota de Entrada" arrow placement="top">
          <i
            className="ph-bold ph-arrow-square-out"
            style={{ color: "#494C62", fontSize: 18 }}
          ></i>
        </Tooltip>
      </IconButton>
    );
  };

  const buscarManifestacaoDestinatario = async function (filtros) {
    setLoading(true);
    const result = await manifestacaoDestinatarioService.getAll(filtros);
    if (!result.isAxiosError) {
      setNotasManifestacao(result.data.rows);
      setRowCount(result.data.count);
    } else {
      responseErros(result);
    }

    setLoading(false);
  };

  const handleErrosDownloadXml = (result) => {
    if (result?.response?.data?.includes("Rejeicao")) {
      notification.erroGenericos(result.response.data);
    } else {
      responseErros(result);
    }
  };

  const onClickBaixarXml = async (chaveXml) => {
    setLoading(true);
    const result = await nfeService.baixarXmlIndividual(
      empresa.cnpjCpf,
      chaveXml,
    );
    if (!result.isAxiosError) {
      notification.sucessoGenericos("Download iniciado com sucesso!");
      const blob = new Blob([result.data], { type: "text/xml" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${chaveXml}.xml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      handleErrosDownloadXml(result);
    }
    setLoading(false);
  };

  async function onClickBaixarDanfe(chaveXml) {
    setLoading(true);
    const resultXml = await nfeService.baixarXmlIndividual(
      empresa.cnpjCpf,
      chaveXml,
    );
    if (!resultXml.isAxiosError) {
      const result = await manifestacaoDestinatarioService.getArquivoDanfe({
        xml: resultXml.data,
      });
      setLoading(false);
      if (!result.isAxiosError) {
        openAbaDanfeBase64(result.data);
      } else {
        responseErros(result);
      }
    } else {
      handleErrosDownloadXml(resultXml);
    }
  }

  async function onClickImportarNfEntrada(chaveXml) {
    setLoading(true);
    const result = await nfeService.baixarXmlIndividual(
      empresa.cnpjCpf,
      chaveXml,
    );
    if (!result.isAxiosError) {
      const blob = new Blob([result.data], { type: "text/xml" });
      window.arquivoXml = blob;
      history.push("/estoque/importacao-xml");
    } else {
      if (result?.response?.data?.includes("Rejeicao")) {
        notification.erroGenericos(result.response.data);
      } else {
        responseErros(result);
      }
    }
    setLoading(false);
  }

  async function onClickViewNfEntrada(id) {
    setLoading(true);
    history.push(`/estoque/nf-entrada/${id}`);
    setLoading(false);
  }

  async function openAbaDanfeBase64(base64Pdf) {
    try {
      const response = await fetch(`data:application/pdf;base64,${base64Pdf}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const pdfWindow = window.open(url, "_blank");
      if (!pdfWindow) {
        notification.alertaGenericos(
          "Popup bloqueado - por favor permita popups para este site",
        );
      }

      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error("Erro ao abrir PDF:", error);
      throw error;
    }
  }

  const buscarPesquisaAvancada = async (filter) => {
    setLoading(true);
    const porIntervalo = [];
    const filtrosAvancadosTemp = {
      ...filter,
    };
    if (
      filtrosAvancadosTemp.dataEmissaoStart &&
      filtrosAvancadosTemp.dataEmissaoEnd
    ) {
      porIntervalo.push({
        coluna: "dataEmissao",
        de: filtrosAvancadosTemp.dataEmissaoStart,
        ate: filtrosAvancadosTemp.dataEmissaoEnd,
      });
    }
    for (const key in filtrosAvancadosTemp) {
      if (filtrosAvancadosTemp[key] == null) {
        delete filtrosAvancadosTemp[key];
      }
    }
    const result = await manifestacaoDestinatarioService.getAllFiltroAvancado({
      ...filtrosAvancadosTemp,
      restritiva: true,
      porIntervalo,
    });
    if (!result.isAxiosError) {
      setNotasManifestacao(result.data.rows);
      setRowCount(result.data.count);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const filtrarListaPorCnpj = (value) => {
    if (value) {
      buscarManifestacaoDestinatario({ cnpj: value });
      setBuscaFiltro("");
    } else {
      buscarManifestacaoDestinatario();
    }
  };

  const sendServerDatagrid = (props) => {
    if (props.tipoFiltro == "simples") {
      buscarManifestacaoDestinatario(props.filtros);
    } else {
      buscarPesquisaAvancada(props.filtros);
    }
  };

  const onClickConsultarManifestacao = async () => {
    setLoading(true);
    const result = await certificadoDigitalService.consultar(empresa.cnpjCpf);
    if (!result.isAxiosError) {
      if (result.data?.certificadoSenha) {
        const motivoConsulta =
          await manifestacaoDestinatarioService.consultarManifestacao({
            certificadoSenha: result.data.certificadoSenha,
          });
        if (
          motivoConsulta.isAxiosError ||
          motivoConsulta?.data?.startsWith("Rejeicao")
        ) {
          notification.erroGenericos(
            motivoConsulta.data ??
              "Erro no servidor. Tente novamente mais tarde.",
          );
        } else if (motivoConsulta?.data?.startsWith("Nenhum")) {
          notification.alertaGenericos(motivoConsulta.data);
        } else {
          notification.sucessoGenericos(motivoConsulta.data);
          buscarManifestacaoDestinatario();
        }
      } else {
        notification.erroGenericos("Certificado não cadastrado!");
      }
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const renderConsultarButton = () => {
    return (
      <span className="d-flex align-items-center">
        <i
          className="ph-bold ph-magnifying-glass"
          style={{ fontSize: 18, marginRight: 10, color: "white" }}
        ></i>
        <span style={{ color: "white", marginRight: 4 }}>{"CONSULTAR"}</span>
      </span>
    );
  };

  const handleRegistrarEvento = () => {
    setOpenRegistroEventoDialog(true);
  };

  return (
    <ListLayout
      title="Manifestação Destinatário"
      loading={loading}
      textoAdicionarCustom={renderConsultarButton()}
      onClickCadastrar={onClickConsultarManifestacao}
      permissaoButton={permissoesHelper.temPermisao(
        "manifestacao-destinatario-visualizar",
      )}
      ButtonMenu={
        <ButtonMenu
          selectionModel={selectionModel}
          handleRegistrarEvento={handleRegistrarEvento}
        />
      }
    >
      <DataGrid
        rows={notasManifestacao}
        columns={colunas}
        paginationMode={"server"}
        rowCount={rowCount}
        disableAcoes
        sendServer={sendServerDatagrid}
        FormFiltroAvancado={FormFiltroAvancadoManifestacao}
        listaStatus={situacoes}
        fieldStatusCustom={"situacao"}
        checkboxSelection
        selectionModel={selectionModel}
        onRowSelectionModelChange={(model) => setSelectionModel(model)}
        FiltroCustom={
          <ButtonFiltroAvancado
            buscaFiltro={buscaFiltro}
            setBuscaFiltro={setBuscaFiltro}
            sendServer={sendServerDatagrid}
            columns={colunas}
            filtrarListaPorCnpj={filtrarListaPorCnpj}
          />
        }
        sx={{ overflowX: "scroll" }}
      />
      <RegistroEventoDialog
        open={openRegistroEventoDialog}
        setOpen={setOpenRegistroEventoDialog}
        empresa={empresa}
        selectionModel={selectionModel}
        setSelectionModel={setSelectionModel}
        notasManifestacao={notasManifestacao}
        buscarManifestacaoDestinatario={buscarManifestacaoDestinatario}
        setLoading={setLoading}
      />
    </ListLayout>
  );
};

export default ManifestacaoDestinatarioView;
